import { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useAwsCredentialsPolling } from '../../../../hooks/useAwsCredentialsPolling';
import { useAwsVendorEnrollInSddsFormSectionQuery } from 'generated/graphql';
import { Link, Banner } from '@tackle-io/platform-ui';
import {
  makeStyles,
  Grid,
  Typography,
  Card,
  CircularProgress,
  Box,
} from 'vendor/material';
import { useAwsIntegrationAccess } from 'hooks/useAwsIntegrationAccess';
import { shortenVendorName } from 'utils/cft';
import AwsLogo from './assets/AwsLogo';
import { useAwsIntegrationsVendorOnboardingRequirementsQuery } from 'generated/graphql';
import SDDSEnrollmentCard from './components/SDDSEnrollmentCard';
import ConnectToAWSCard from './components/ConnectToAWSCard';
import AwsConnectionStatusCard from './components/AwsConnectionStatusCard';
import { useLocation } from 'react-router-dom';

const MAX_CONNECTION_TIME = 900000; // 15 minutes in milliseconds

const useStyles = makeStyles((theme) => ({
  breadcrumbIntegrations: {
    paddingBottom: '20px',
    marginLeft: theme.spacing(2),
    color: theme.palette.NEUTRAL900,
    textDecoration: 'none',
    '& > a': {
      textDecoration: 'none',
      '&:hover': {
        color: theme.palette.BLUE400,
      },
      '&:visited': {
        textDecoration: 'none',
        color: theme.palette.NEUTRAL900,
      },
    },
  },
  breadcrumbDivider: {
    marginLeft: theme.spacing(3),
  },
  headerGrid: {
    marginLeft: theme.spacing(2),
    paddingLeft: theme.typography.pxToRem(100),
    marginTop: theme.typography.pxToRem(50),
    marginBottom: theme.typography.pxToRem(50),
  },
  headerTitle: {
    color: theme.palette.NEUTRAL700,
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  cardContainer: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  marginLeft20: {
    marginLeft: theme.spacing(2),
  },
}));

export const AWS_VENDOR_REQUIREMENTS_QUERY = gql`
  query AwsIntegrationsVendorOnboardingRequirements {
    currentUser {
      id
      vendor {
        id
        name
        external_id
      }
    }
    awsRegistrationToken
  }
`;

const AwsIntegrations = () => {
  const classes = useStyles();
  const location = useLocation();
  const [hasClickedConnect, setHasClickedConnect] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasConnectionTimedOut, setHasConnectionTimedOut] = useState(false);
  const {
    credentials,
    hasStep1Complete,
    startPolling,
    isLoading: isCredentialsLoading,
    isError: isCredentialsError,
  } = useAwsCredentialsPolling();
  const { data } = useAwsIntegrationsVendorOnboardingRequirementsQuery();
  const { data: sddsData, loading: sddsLoading } =
    useAwsVendorEnrollInSddsFormSectionQuery({
      context: { batchKey: 'vendor-onboarding' },
      fetchPolicy: 'cache-and-network', // Always fetch from network but use cache if available
    });

  // Check if SDDS enrollment is complete
  const stacks = sddsData?.currentUser?.vendor?.onboarding?.aws?.stacks || [];
  const isSDDSEnrollComplete = stacks.includes('sdds-enroll');

  // Check if both steps are complete
  const isBothStepsComplete = hasStep1Complete && isSDDSEnrollComplete;

  // We don't need to stop polling here anymore, as it's handled in the useAwsCredentialsPolling hook
  // The hook will stop polling when all three SDDS keys have actual values

  // Set loading state based on credentials and SDDS data
  useEffect(() => {
    // Only set loading to false when we have all the data and it's not still loading
    if (!isCredentialsLoading && sddsData && !sddsLoading) {
      setIsLoading(false);
    } else {
      // If any data is missing or still loading, set loading to true
      setIsLoading(true);
    }
  }, [isCredentialsLoading, sddsData, sddsLoading]);

  // Success notifications are handled in the SDDS enrollment completion

  // We no longer stop polling when component unmounts
  // The polling will continue in the background until it completes or times out

  // Check URL for cloudformation param to determine if user has clicked connect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.has('stackName') &&
      params.get('stackName') === 'Tackle-Resources'
    ) {
      setHasClickedConnect(true);

      // Start polling if we detect the user has connected via URL params
      startPolling();
    }
  }, [location, startPolling]);

  const hasRequiredData = (): boolean => {
    return !!(
      data?.awsRegistrationToken &&
      data?.currentUser?.vendor?.name &&
      data?.currentUser?.vendor?.external_id
    );
  };

  const generateUrl = () => {
    const stackName = 'Tackle-Resources';
    const templateURL =
      'https://tackle-templates.s3.us-west-2.amazonaws.com/consolidated/consolidated.yaml'; // change to the dev one after we merge the PR

    const searchParams = new URLSearchParams({
      stackName,
      templateURL,
      param_RegistrationToken: data?.awsRegistrationToken || '',
      param_CompanyName:
        shortenVendorName(data?.currentUser?.vendor?.name) || '',
      param_ExternalId: data?.currentUser?.vendor?.external_id || '',
    });

    return `https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/quickcreate?${searchParams.toString()}`;
  };

  const isAwsCloudConnectorEnabled = useAwsIntegrationAccess();

  if (!isAwsCloudConnectorEnabled) {
    return <Redirect to="/integrations" />;
  }

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      data-testid="aws-integrations"
    >
      <Grid item xs={12} className={classes.headerGrid}>
        <span className={classes.breadcrumbDivider}>{'<'}</span>
        <Link
          to="/integrations"
          aria-label="breadcrumb"
          className={classes.breadcrumbIntegrations}
        >
          Integrations
        </Link>
        <Grid container>
          <Grid item className={classes.marginLeft20}>
            <AwsLogo />
          </Grid>
          <Grid item>
            <Typography className={classes.headerTitle} variant="h4">
              AWS
            </Typography>
          </Grid>
        </Grid>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
            width="100%"
          >
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Grid item>
            <AwsConnectionStatusCard
              isVisible={
                hasClickedConnect &&
                !credentials.s3Arn &&
                !hasConnectionTimedOut
              }
              credentials={credentials}
            />
            {/* Show error banner whenever assume role fails or there's a query error */}
            {(credentials.lastAssumeRoleStatus === 'failed' ||
              isCredentialsError) &&
              !hasConnectionTimedOut && (
                <Card className={classes.cardContainer}>
                  <Banner
                    data-testid="aws-connection-error"
                    type="danger"
                    title="Tackle failed to connect to AWS"
                    body={`${credentials.lastAssumeRoleError}
                    Once resolved, click "connect" to try again. If the issue still persists, 
                    contact Tackle Support.`}
                    borderPosition="top"
                    isCollapsible={true}
                  />
                </Card>
              )}
            <Card className={classes.cardContainer}>
              <ConnectToAWSCard
                generateUrl={generateUrl}
                hasRequiredData={hasRequiredData()}
                onConnect={() => {
                  setHasClickedConnect(true);
                  setHasConnectionTimedOut(false);

                  // Start polling for credentials
                  startPolling();

                  // We don't need to store connection timeout in localStorage anymore
                  // The timeout UI state is handled locally in this component

                  setTimeout(() => {
                    setHasConnectionTimedOut(true);
                  }, MAX_CONNECTION_TIME);
                }}
                isStepComplete={hasStep1Complete}
                lastAssumeRoleStatus={credentials.lastAssumeRoleStatus}
                disableConnect={
                  isLoading ||
                  hasStep1Complete ||
                  (hasClickedConnect &&
                    !credentials.s3Arn &&
                    !hasConnectionTimedOut)
                }
                isBothStepsComplete={isBothStepsComplete}
              />
            </Card>
            <Card className={classes.cardContainer}>
              <SDDSEnrollmentCard isConnectButtonClicked={hasClickedConnect} />
            </Card>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default AwsIntegrations;
